import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { ChevronDown, ChevronRight } from 'lucide-react';

import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';

const hasActiveChild = (item, pathname) =>
  (item.children || []).some((c) => (c.url && matchPath({ path: c.url, end: false }, pathname)) || hasActiveChild(c, pathname));

// ==============================|| NAVIGATION - LIST GROUP (light ERP sidebar) ||============================== //

export default function NavGroup({ item }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();
  const [open, setOpen] = useState({});

  // the group that contains the current page is open
  useEffect(() => {
    const next = {};
    (item.children || []).forEach((c) => {
      if (c.type === 'collapse' && hasActiveChild(c, pathname)) next[c.id] = true;
    });
    setOpen((o) => ({ ...o, ...next }));
  }, [pathname, item]);

  const rows = item.children?.map((menuItem) => {
    if (menuItem.type === 'collapse') {
      const Icon = menuItem.icon;
      const isOpen = !!open[menuItem.id];
      const active = hasActiveChild(menuItem, pathname);
      const button = (
        <ListItemButton
          onClick={() => setOpen((o) => ({ ...o, [menuItem.id]: !o[menuItem.id] }))}
          sx={(theme) => ({
            minHeight: 34,
            py: 0.5,
            my: '1px',
            borderRadius: '6px',
            color: active ? theme.vars.palette.grey[900] : theme.vars.palette.grey[700],
            ...(drawerOpen ? { mx: 1, pl: 1.25, pr: 1 } : { mx: 'auto', width: 40, height: 40, justifyContent: 'center', px: 0 }),
            '&:hover': { backgroundColor: theme.vars.palette.grey[100] }
          })}
        >
          {Icon && (
            <ListItemIcon sx={{ minWidth: drawerOpen ? 26 : 0, color: active ? 'primary.main' : 'grey.500', justifyContent: 'center' }}>
              <Icon size={17} strokeWidth={1.6} />
            </ListItemIcon>
          )}
          {drawerOpen && <ListItemText primary={menuItem.title} primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 600 : 500, noWrap: true }} />}
          {drawerOpen && (isOpen ? <ChevronDown size={14} color="#8c8c8c" /> : <ChevronRight size={14} color="#8c8c8c" />)}
        </ListItemButton>
      );
      return (
        <Box key={menuItem.id}>
          {drawerOpen ? button : <Tooltip title={menuItem.title} placement="right" arrow>{button}</Tooltip>}
          <Collapse in={isOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menuItem.children?.map((child) => (
                <NavItem key={child.id} item={child} level={2} />
              ))}
            </List>
          </Collapse>
        </Box>
      );
    }
    return <NavItem key={menuItem.id} item={menuItem} level={1} />;
  });

  return (
    <List
      subheader={
        item.title && drawerOpen ? (
          <Box sx={{ px: 2.25, pt: 1.5, pb: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'grey.500' }}>
              {item.title}
            </Typography>
          </Box>
        ) : null
      }
      sx={{ py: 0, mb: 0.5 }}
    >
      {rows}
    </List>
  );
}

NavGroup.propTypes = { item: PropTypes.object };
